import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { AlertCircle, Mail, ArrowLeft } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Navbar } from "../components/Navbar";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const { forgotPassword } = useAuth(); 
  const navigate = useNavigate();

  const handleSend = async () => {
    setError("");
    setSuccess("");
    setIsLoading(true);

    if (!email) {
      setError("Please enter your email.");
      setIsLoading(false);
      return;
    }

    try {
      const result = await forgotPassword(email);
      if (result.success) {
        setSuccess( "Password reset instructions sent! Check your email. " +
        "If you don't see it, check your spam or junk folder.");
      } else {
        setError(result.error || "Failed to send reset instructions.");
      }
    } catch {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />

      <main className="flex-1 flex flex-col items-center justify-center px-4">
        {/* Back Button */}
        <div className="w-full max-w-md mb-4 flex justify-start">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft size={20} /> Back
          </button>
        </div>

        {/* Title & Description */}
        <div className="max-w-md text-center mb-6">
          <h2 className="text-3xl font-bold mb-2">Reset Password</h2>
          <p className="text-sm text-gray-600">
            Enter your email to receive password reset instructions.
          </p>
        </div>

        {/* Card */}
        <div className="w-full max-w-md bg-white p-8 rounded-xl shadow-md border border-gray-200">
          <div className="space-y-4">
            {/* Email Input */}
            <div className="relative">
              <Label htmlFor="email">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-12 focus-visible:ring-igbanwo-mint mt-1"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Error & Success */}
            {error && (
              <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className="p-3 text-sm text-green-600 bg-green-50 border border-green-200 rounded-md">
                {success}
              </div>
            )}

            {/* Send Button */}
            <Button
              onClick={handleSend}
              className="w-full bg-igbanwo-mint hover:bg-igbanwo-mint/90 text-white"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Sending...
                </div>
              ) : (
                "Send Reset Request"
              )}
            </Button>
          </div>

          {/* Sign In Link */}
          <div className="mt-6 text-center text-sm">
            <p className="text-gray-600">
              Remembered your password?{" "}
              <button
                type="button"
                className="text-igbanwo-mint hover:text-igbanwo-mint/80 font-medium"
                onClick={() => navigate("/auth?mode=login")}
              >
                Sign in here
              </button>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ForgotPassword;
